"use client";

/**
 * Global checkout-return fallback.
 *
 * A purchase should return the user to the page they started it from — that is
 * the whole point of `returnTo`. Before this existed, only a handful of routes
 * knew how to handle a Stripe return (`?upgraded` / `?topup` / `?trial` +
 * `session_id`), so every surface that could be opened from anywhere — most
 * visibly the header credits chip — had to hard-code `returnTo="/settings/billing"`
 * to guarantee the purchase reconciled and confirmed at all. The cost was the
 * reported bug: raise your monthly credits while browsing your projects and you
 * were dumped onto Settings → Billing with no success confirmation.
 *
 * This host closes that gap. Mounted at the app root, it reconciles and shows
 * the confirmation on ANY route, so `returnTo` is free to be "wherever you
 * were". Routes that already own their return (see `ownsCheckoutReturn`) are
 * skipped, so nothing reconciles or confirms twice.
 */

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import { showPurchaseConfirmation } from "./purchase-confirmation-store";
import { customToast } from "@/lib/toast";
import {
  clearReturnRefParams,
  decideConfirmation,
  ownsCheckoutReturn,
  readCheckoutReturn,
  settleCheckoutReturn,
  trialFailureMessage,
  type EntitlementSnapshot,
  type ReconcileResult,
} from "@/lib/billing/checkout-return";
import { announcePendingPurchase } from "@/lib/billing/pending-purchase-watch";
import {
  beginEntitlementSync,
  broadcastCredits,
} from "@/lib/billing/entitlement-refresh";
import { paymentProvider } from "@/lib/payments/provider";

/** Drop the return flags so a refresh or back-navigation can't replay them. */
function stripReturnParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  // The TYPE flags are this file's own; every processor's REF param comes from
  // clearReturnRefParams, so Chargebee's id+state and Whop's payment_id /
  // receipt_id / membership_id are stripped without this list having to know
  // they exist. The old inline list knew about Stripe and Chargebee only.
  for (const key of ["trial", "topup", "upgraded", "subscribed", "card_added"]) {
    url.searchParams.delete(key);
  }
  clearReturnRefParams(url);
  window.history.replaceState({}, "", url.toString());
}

export function CheckoutReturnHost() {
  const pathname = usePathname();
  // A checkout return arrives on a full page load, so this fires at most once
  // per mount. The ref also absorbs StrictMode's double-invocation, which would
  // otherwise reconcile twice and show the modal twice.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    if (typeof window === "undefined") return;
    if (ownsCheckoutReturn(pathname ?? "")) return;

    const { type, sessionId, subscribedValue } = readCheckoutReturn(
      window.location.search,
    );
    // Not a checkout return at all → leave the URL alone.
    if (!type) return;
    // A saved-card purchase carries no session (it was fulfilled server-side)
    // and hands its confirmation over through sessionStorage, which
    // <PurchaseConfirmationHost/> replays — confirming here too would double it.
    // A card-only return and a cancelled checkout have nothing to celebrate.
    if (!sessionId || type === "card_added" || subscribedValue === "cancelled") {
      stripReturnParams();
      return;
    }

    ran.current = true;
    // Visible for the whole wait, not just once it has gone wrong — see
    // beginEntitlementSync. Released in the .finally below, including on a
    // throw: a latch left up would leave the chip saying "confirming" forever.
    const releaseSync = beginEntitlementSync();
    (async () => {
      // Reconcile, then WAIT for the entitlement — the shared budget, not a
      // single read. This host is the fallback for every route that does not
      // own its own return, so it is the surface most likely to meet a slow
      // webhook, and it was the one deciding fastest.
      const { reconcile, fresh } = await settleCheckoutReturn({
        isTrial: type === "trial",
        reconcile: async () => {
          // Idempotent with the webhook — a fallback for a missed/slow
          // delivery, never a second grant.
          const res = await fetch("/api/billing/reconcile", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            // WHICH PROCESSOR MINTED THIS REF. Paddle subscription ids are also
            // `sub_`-prefixed and Chargebee's are opaque, so the server cannot
            // infer it — omitting this defaults it to Stripe, which is what left
            // the Chargebee arm unreachable from a hosted return. The flag is the
            // right source here because a return page only ever sees a ref the
            // ACTIVE provider just minted.
            body: JSON.stringify({ sessionId, provider: paymentProvider() }),
          });
          return (await res.json().catch(() => null)) as ReconcileResult | null;
        },
        // Read AFTER reconcile so the confirmation names the plan and
        // allocation that are actually persisted.
        readEntitlement: async () => {
          const r = await fetch("/api/credits", { cache: "no-store" });
          return r.ok ? ((await r.json()) as EntitlementSnapshot) : null;
        },
      });

      // Same reason as every other in-page return: the nav chip took its one
      // reading before this purchase existed.
      broadcastCredits(fresh);
      const decision = decideConfirmation({
        returnType: type,
        reconcile,
        fresh,
        source: "global_checkout_return",
      });
      if (decision.status === "confirm") {
        showPurchaseConfirmation(decision.input);
      } else if (decision.status === "trial_failed") {
        customToast.error(trialFailureMessage(decision.reason));
      } else if (
        decision.status === "trial_pending" ||
        decision.status === "upgrade_pending"
      ) {
        // Amber, not red: the payment very likely succeeded and the entitlement
        // is still landing. Red would tell a paying customer they were refunded,
        // and a success modal would tell them they were on Pro before they were
        // — the false confirmation that left the upgrade CTA live to be clicked
        // a second time (PANDA-2292). So say what is true, keep watching, and
        // confirm for real when it arrives.
        void announcePendingPurchase({
          kind: decision.status === "trial_pending" ? "trial" : "upgrade",
          returnType: type,
          reconcile,
          source: "global_checkout_return",
        });
      }
    })().finally(() => {
      releaseSync();
      stripReturnParams();
    });
  }, [pathname]);

  return null;
}
